import { Link } from 'react-router-dom';
import { Callout, DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'before-you-begin', label: 'Before you begin' },
  { id: 'install', label: '1. Install ClipRadar' },
  { id: 'first-launch', label: '2. First launch' },
  { id: 'add-footage', label: '3. Add footage' },
  { id: 'analyze', label: '4. Analyze' },
  { id: 'activate-trial', label: '5. Activate your trial' },
  { id: 'search', label: '6. Search' },
  { id: 'premiere-next', label: 'Put it in Premiere' },
];

export default function Body() {
  return (
    <>
      <DocSection id="before-you-begin" title="Before you begin">
        <p>
          ClipRadar is a <strong>Windows 10/11 (64-bit)</strong> desktop app and it <strong>requires an
          NVIDIA GPU (CUDA)</strong> — every analysis pass runs on your GPU, search runs locally, and the
          app does not run without one. See <Link to="/docs/reference/system-requirements">System
          requirements</Link> for the full specs.
        </p>
      </DocSection>

      <DocSection id="install" title="1. Install ClipRadar">
        <p>
          Grab the installer from the <Link to="/download">download page</Link> and run it. It&rsquo;s a tiny{' '}
          <span style={kbd}>nsis-web</span> stub that pulls the real ~3.2&nbsp;GB app payload over the internet
          during install — stay connected until it finishes.
        </p>
        <p>
          Windows will flag it with a <strong>SmartScreen</strong> &ldquo;unknown publisher&rdquo; warning —
          expected, since the beta isn&rsquo;t code-signed yet. Click <strong>More info</strong>, then{' '}
          <strong>Run anyway</strong>.
        </p>
        <DocShot
          slug="quickstart"
          shot="smartscreen"
          alt="Windows SmartScreen unknown-publisher dialog with More info → Run anyway"
          caption="You'll see this again on future updates too — that's normal."
        />
        <p>
          For the full installer walkthrough — where files land, how updating works — see{' '}
          <Link to="/docs/install">Download &amp; install</Link>.
        </p>
      </DocSection>

      <DocSection id="first-launch" title="2. First launch">
        <p>
          On first launch ClipRadar downloads about <strong>13&nbsp;GB</strong> of AI models in the background
          — the visual, speech, and face models it needs to analyze footage. You don&rsquo;t have to wait: you
          can add footage and start building your library right away.
        </p>
        <DocShot slug="quickstart" shot="first-run" alt="First-run splash with the background model-download banner" />
        <p>
          The first-run wizard also runs a quick <strong>hardware check</strong>, confirming ClipRadar found
          your NVIDIA GPU and how it fits your footage.
        </p>
        <DocShot slug="quickstart" shot="hardware-fit" alt="Hardware-fit verdict card in the first-run wizard" />
      </DocSection>

      <DocSection id="add-footage" title="3. Add footage">
        <p>
          Open the <strong>Media</strong> tab and drag files or folders in — or drop them anywhere in the
          window, since the whole app is a drop target. ClipRadar references your footage where it already
          lives; nothing is copied or moved.
        </p>
        <DocShot slug="quickstart" shot="media-drop" alt="Dragging footage into the Media tab" />
      </DocSection>

      <DocSection id="analyze" title="4. Analyze">
        <p>
          Select your new sources and run <strong>Analyze</strong>. ClipRadar runs up to three passes on your
          GPU — <strong>Visual</strong> (scene detection + embeddings), <strong>Speech</strong>
          (transcription), and <strong>Faces</strong> (detection + clustering) — and you can search whatever
          has finished while the rest keeps analyzing in the background.
        </p>
        <DocShot slug="quickstart" shot="analyze-bar" alt="The Analyze bar over selected media" />
      </DocSection>

      <DocSection id="activate-trial" title="5. Activate your trial">
        <p>
          Search is licensed, so activate before your first query. Every new user gets a{' '}
          <strong>free trial of 40 searches</strong>, keyed by email — no card required. Start the trial or
          paste a purchased key on the wizard&rsquo;s last step, or later from{' '}
          <Link to="/docs/interface/settings">Settings → Licensing</Link>.
        </p>
        <DocShot slug="quickstart" shot="license" alt="License entry in the first-run wizard" />
        <Callout>
          Running out of trial searches doesn&rsquo;t block you with an error — it shows a used-up card with a
          clear upgrade path. See <Link to="/docs/concepts/licensing">Licensing &amp; activation</Link> for
          every state.
        </Callout>
      </DocSection>

      <DocSection id="search" title="6. Search">
        <p>
          Open the <strong>Search</strong> tab and type what you remember — what&rsquo;s on screen, what
          someone said, or who&rsquo;s in frame. Results come back as scenes, best match first.
        </p>
        <DocShot slug="quickstart" shot="result" alt="A search result card" />
        <p>
          Not finding it on the first try? <Link to="/docs/guides/search-best-practices">Get better search
          results</Link> covers phrasing, scopes, and filters.
        </p>
      </DocSection>

      <DocSection id="premiere-next" title="Put it in Premiere">
        <p>
          Everything above works the same inside Adobe Premiere Pro. Install the panel once and search, place
          clips, and jump to the Source Monitor without leaving your edit.
        </p>
        <div style={{ marginTop: 8, padding: '20px 22px', borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--fg-1)' }}>The Adobe Premiere Pro panel</div>
            <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 3 }}>Install the <span style={kbd}>.ccx</span>, then search and place clips without leaving Premiere.</div>
          </div>
          <Link to="/docs/platforms/premiere-panel" style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--signal-400)', textDecoration: 'none', borderBottom: 'none', whiteSpace: 'nowrap' }}>Set up the panel →</Link>
        </div>
      </DocSection>
    </>
  );
}
